
import { supabase } from '@/integrations/supabase/client';

// Export the supabase client directly from the auto-generated file
export { supabase };

// Helper function to check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  // We can check if the URL is a valid URL
  try {
    return !!supabase && !!supabase.auth;
  } catch (error) {
    console.error('Error checking Supabase configuration:', error);
    return false;
  }
};

// Helper function to refresh user profile data
export const refreshUserProfile = async (userId: string) => {
  try {
    if (!userId) {
      return { success: false, error: "Missing userId" };
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error("Error refreshing user profile:", error);
      return { success: false, error };
    }
    
    return { success: true, data };
  } catch (error) {
    console.error("Exception refreshing user profile:", error);
    return { success: false, error };
  }
};

// Helper function to get the current user's profile picture URL
export const getUserProfilePicture = async (userId: string): Promise<string | null> => {
  try {
    if (!userId) return null;
    
    const { data, error } = await supabase
      .from('profiles')
      .select('avatar_url')
      .eq('id', userId)
      .single();
    
    if (error || !data) {
      console.error("Error fetching profile picture URL:", error);
      return null;
    }
    
    return data.avatar_url;
  } catch (error) {
    console.error("Exception getting user profile picture:", error);
    return null;
  }
};

export const uploadProfilePicture = async (
  userId: string,
  file: File,
  fullName: string
): Promise<{ success: boolean; url?: string; error?: any }> => {
  try {
    // Input validation
    if (!userId) return { success: false, error: "Missing userId" };
    if (!file || !(file instanceof File)) {
      console.error('Invalid file object:', { file, type: typeof file });
      return { success: false, error: "Invalid or missing file object" };
    }
    if (!file.type.startsWith('image/')) {
      console.error('Invalid file type:', file.type);
      return { success: false, error: "Invalid file type. Please upload an image." };
    }
    if (file.size > 5 * 1024 * 1024) {
      console.error('File too large:', file.size);
      return { success: false, error: "File too large. Please upload under 5MB." };
    }

    // Create folder name from user's name: convert to lowercase and replace spaces with hyphens
    const folderName = fullName.toLowerCase().replace(/\s+/g, '-');
    const uniqueFolderName = `${folderName}-${userId.slice(0, 6)}`;
    const filePath = `${uniqueFolderName}/profile-${Date.now()}.${file.name.split('.').pop()}`;
    
    // Upload the file directly without any preprocessing
    const { data, error: uploadError } = await supabase.storage
      .from('profile-picture')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      console.error("Upload failed:", uploadError);
      return { success: false, error: uploadError };
    }

    // Get the public URL
    const { data: { publicUrl } } = supabase.storage
      .from('profile-picture')
      .getPublicUrl(data.path);

    // Update profile with new avatar URL
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', userId);

    if (updateError) {
      console.error('Profile update failed:', updateError);
      return { success: false, error: updateError };
    }

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error('Unexpected error during profile picture upload:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : `Unknown error: ${String(error)}`
    };
  }
};

// Helper function to create a user in Supabase
export const createSupabaseUser = async (email: string, password: string, userData: any) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    });
    
    if (error) {
      console.error("Error creating Supabase user:", error);
      return { success: false, error };
    }
    
    return { success: true, data };
  } catch (error) {
    console.error("Exception creating Supabase user:", error);
    return { success: false, error };
  }
};

// Helper function to fetch Supabase users
export const fetchSupabaseUsers = async () => {
  try {
    // Fetch from profiles table which is accessible via the client
    const { data, error } = await supabase
      .from('profiles')
      .select('*');
    
    if (error) {
      console.error("Error fetching Supabase users:", error);
      return { success: false, error };
    }
    
    return { success: true, data };
  } catch (error) {
    console.error("Exception fetching Supabase users:", error);
    return { success: false, error };
  }
};

// Helper function to create or get an existing conversation
export const createOrGetConversation = async (mentorId: string, menteeId: string) => {
  try {
    if (!mentorId || !menteeId) {
      console.error("Missing user IDs:", { mentorId, menteeId });
      return { success: false, error: "Missing user IDs" };
    }
    
    // Check for existing conversation
    const { data: existingConv, error: checkError } = await supabase
      .from('conversations')
      .select('id')
      .or(`and(mentor_id.eq.${mentorId},mentee_id.eq.${menteeId}),and(mentor_id.eq.${menteeId},mentee_id.eq.${mentorId})`)
      .maybeSingle();
      
    if (checkError) {
      console.error("Error checking for existing conversation:", checkError);
      return { success: false, error: checkError };
    }
    
    // If conversation exists, return it
    if (existingConv) {
      console.log("Found existing conversation:", existingConv);
      return { success: true, data: { id: existingConv.id, isNew: false } };
    }
    
    // Create new conversation
    console.log("Creating new conversation between", mentorId, "and", menteeId);
    const { data: newConv, error: createError } = await supabase
      .from('conversations')
      .insert({
        mentor_id: mentorId,
        mentee_id: menteeId,
        last_message_text: 'Conversation started',
        last_message_time: new Date().toISOString()
      })
      .select('id')
      .single();
      
    if (createError) {
      console.error("Error creating conversation:", createError);
      return { success: false, error: createError };
    }
    
    console.log("Created new conversation:", newConv);
    return { success: true, data: { id: newConv.id, isNew: true } };
  } catch (error) {
    console.error("Exception in createOrGetConversation:", error);
    return { success: false, error };
  }
};

// Helper function to send a message in a conversation
export const sendMessage = async (conversationId: string, senderId: string, content: string, attachmentUrl?: string, attachmentName?: string) => {
  try {
    if (!conversationId || !senderId) {
      return { success: false, error: "Missing required fields" };
    }

    // Allow empty content if there's an attachment
    if (!content.trim() && !attachmentUrl) {
      return { success: false, error: "Message must have content or attachment" };
    }
    
    console.log(`Sending message to conversation ${conversationId} from ${senderId}: "${content}"`);
    
    const messageData = {
      conversation_id: conversationId,
      sender_id: senderId,
      content: content || "", // Allow empty content for attachment-only messages
      is_read: false
    };
    
    if (attachmentUrl) {
      Object.assign(messageData, { 
        attachment_url: attachmentUrl,
        attachment_name: attachmentName || "Attachment" 
      });
    }
    
    const { data, error } = await supabase
      .from('messages')
      .insert(messageData)
      .select('id')
      .single();
      
    if (error) {
      console.error("Error sending message:", error);
      return { success: false, error };
    }
    
    console.log("Message sent successfully:", data.id);
    
    // Update the conversation's last message
    await supabase
      .from('conversations')
      .update({
        last_message_text: content || (attachmentName ? `📎 ${attachmentName}` : "Attachment"),
        last_message_time: new Date().toISOString()
      })
      .eq('id', conversationId);
      
    return { success: true, data };
  } catch (error) {
    console.error("Exception sending message:", error);
    return { success: false, error };
  }
};

// Helper function to subscribe to real-time message updates
export const subscribeToMessages = (conversationId: string, callback: (payload: any) => void) => {
  console.log(`Subscribing to real-time updates for conversation: ${conversationId}`);
  
  const channel = supabase
    .channel(`messages:${conversationId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `conversation_id=eq.${conversationId}`
    }, (payload) => {
      console.log("Real-time message received:", payload);
      callback(payload);
    })
    .subscribe((status) => {
      console.log(`Supabase channel status: ${status}`);
    });
    
  return channel;
};
